import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { DisplayOrder, StyledJobOffers, StyledJobOffersProps } from './styled'
import { API } from 'javascripts/api'
import { MasterSkill, JobOfferCategory, SeoContentCategory, SeoContentMasterSkill, Skill, SkillCategory, SkillType, UserJobOffer } from 'javascripts/api/generated/api'
import { CheckBoxProps } from 'spa/member/components/Checkbox/styled'
import {
  OptionGroup,
  StyledSearchConditionDropdownProps,
  StyledSearchConditionDropdownPropsWithId
} from 'spa/member/components/JobOfferSearchForm/SearchConditionDropdown/styled'
import { SearchWithOtherQueryProps } from 'spa/member/components/SearchWithOtherQuery/styled'
import { Head } from 'spa/config/Head'
import MemberLayout from 'spa/member/layouts/MemberLayout/Index'
import { SessionContext } from 'spa/App'
import TagManager from 'react-gtm-module'
import { StyledLoadingOverlay } from '../../components/LoadingOverlay/styled'
import { UserJobOfferWithInfo } from 'spa/member/types/userJobOfferWithInfo'
import { decorate } from 'spa/member/decorators/jobOfferDecorator'
import { SeoMemberTechCompanyName } from 'spa/member/constants/companyName'

const useQuery = (): URLSearchParams => {
  return new URLSearchParams(useLocation().search)
}

const Index: React.FC = () => {
  const session = React.useContext(SessionContext)
  const queries = useQuery()
  const navigate = useNavigate()

  const loggedOut = !session?.user

  const { skillId } = useParams<{ skillId: string }>()
  const { categoryId } = useParams<{ categoryId: string }>()
  const { prefectureId } = useParams<{ prefectureId: string }>()
  const { workingDayId } = useParams<{ workingDayId: string }>()
  const { workStyleId } = useParams<{ workStyleId: string }>()
  const { appealId } = useParams<{ appealId: string }>()

  const word = useRef<string>(queries.get('word') || undefined)
  const minPrice = useRef<number>(parseInt(queries.get('min_price')) || undefined)
  const maxPrice = useRef<number>(parseInt(queries.get('max_price')) || undefined)
  const openonly = useRef<boolean>(queries.get('openonly') === null || queries.get('openonly') === '' || queries.get('openonly') === 'true')
  const selectedWorkingDayIds = useRef<number[]>(queries.get('working_days') ? queries.get('working_days').split(',').map(Number) : workingDayId ? [Number(workingDayId)] : undefined)
  const selectedWorkStyleIds = useRef<number[]>(queries.get('work_styles') ? queries.get('work_styles').split(',').map(Number) : workStyleId ? [Number(workStyleId)] : undefined)
  const selectedCategoryIds = useRef<number[]>(queries.get('category_ids') ? queries.get('category_ids').split(',').map(Number) : categoryId ? [Number(categoryId)] : undefined)
  const selectedPrefectureIds = useRef<number[]>(queries.get('prefecture_ids') ? queries.get('prefecture_ids').split(',').map(Number) : prefectureId ? [Number(prefectureId)] : undefined)
  const selectedSkillIds = useRef<number[]>(queries.get('skill_ids') ? queries.get('skill_ids').split(',').map(Number) : skillId ? [Number(skillId)] : undefined)
  const selectedAppealIds = useRef<number[]>(queries.get('appeal_ids') ? queries.get('appeal_ids').split(',').map(Number) : appealId ? [Number(appealId)] : undefined)
  const order = useRef<DisplayOrder>(queries.get('order') as DisplayOrder || 'max_salary_desc')
  const page = useRef<number>(parseInt(queries.get('page')) || 1)

  const userJobOffers = useRef<UserJobOffer[]>([])
  const [jobOffers, setJobOffers] = useState<UserJobOfferWithInfo[]>([])
  const [count, setCount] = useState<number>(0)
  const [pageCount, setPageCount] = useState<number>(0)
  const [pageTitle, setPageTitle] = useState<string>(`フリーランスの求人・案件一覧 | ${SeoMemberTechCompanyName}`)
  const [pageDescription, setPageDescription] = useState<string>('フリーランスの求人・案件一覧ページです。人気のリモート・高単価・週3～4日案件多数。フリーランス・業務委託として働きたいエンジニア・デザイナー特化の求人サイト。')
  const [h1, setH1] = useState<string>('')
  const [prevUrl, setPrevUrl] = useState<string>('')
  const [nextUrl, setNextUrl] = useState<string>('')
  const [canonicalUrl, setCanonicalUrl] = useState<string>('')
  const [seoContentCategory, setSeoContentCategory] = useState<{ title: string, seo: SeoContentCategory }>()
  const [seoContentMasterSkill, setSeoContentMasterSkill] = useState<{ title: string, seo: SeoContentMasterSkill }>()
  const [workingDays, setWorkingDays] = useState<CheckBoxProps[]>([])
  const [workStyles, setWorkStyles] = useState<CheckBoxProps[]>([])
  const [breadcrumb, setBreadcrumb] = useState<string>('案件一覧ページ')
  const [categories, setCategories] = useState<StyledSearchConditionDropdownProps[]>(null)
  const [prefectures, setPrefectures] = useState<StyledSearchConditionDropdownProps[]>(null)
  const [skills, setSkills] = useState<StyledSearchConditionDropdownProps[]>(null)
  const [appeals, setAppeals] = useState<CheckBoxProps[]>(null)
  const [recommendedCategories, setRecommendedCategories] = useState<SearchWithOtherQueryProps>(null)
  const [recommendedSkills, setRecommendedSkills] = useState<SearchWithOtherQueryProps>(null)
  const [loading, setLoading] = useState<boolean>(false)

  // SEO情報の取得
  const fetchJobOffersSeo = async () => {
    const response = await API.getJobOffersSeo(
      selectedCategoryIds.current,
      selectedSkillIds.current,
      page.current,
      selectedPrefectureIds.current,
      word.current,
      selectedWorkingDayIds.current,
      selectedWorkStyleIds.current,
      selectedAppealIds.current,
    )

    const json = response.data
    setPageTitle(json.title)
    setPageDescription(json.description)
    setBreadcrumb(json.breadcrumb)
    setH1(json.h1)
    setPrevUrl(json.prev_url)
    setNextUrl(json.next_url)
    setCanonicalUrl(json.canonical_url)

    const newSeoContentCategory = json.seo_content_category ? { title: `${json.theme}のお仕事をお探しの方`, seo: json.seo_content_category } : undefined
    setSeoContentCategory(newSeoContentCategory)
    const newSeoContentMasterSkill = json.seo_content_master_skill ? { title: json.theme, seo: json.seo_content_master_skill } : undefined
    setSeoContentMasterSkill(newSeoContentMasterSkill)
  }

  const updateDataLayer = (
    categoryIds?: number[],
    prefectureIds?: number[],
    skillIds?: number[],
    minPrice?: number,
    workingDays?: number[]
  ) => {
    const dataLayer = { categoryIds, prefectureIds, skillIds, minPrice, workingDays }
    TagManager.dataLayer({ dataLayer })
  }

  const onSearch = (
    newWord?: string,
    newMinPrice?: number,
    newMaxPrice?: number,
    newOpenonly?: boolean,
    workingDayIds?: number[],
    workStyleIds?: number[],
    categoryIds?: number[],
    prefectureIds?: number[],
    skillIds?: number[],
    appealIds?: number[],
    newOrder?: DisplayOrder,
    newPage?: number,
    options?: { willUpdateDataLayer: boolean }
  ) => {
    const params = {
      word: newWord ?? undefined,
      min_price: typeof newMinPrice === 'number' ? String(newMinPrice) : undefined,
      max_price: typeof newMaxPrice === 'number' ? String(newMaxPrice) : undefined,
      openonly: typeof newOpenonly === 'boolean' ? String(newOpenonly) : undefined,
      working_days: workingDayIds && workingDayIds.length && workingDayIds?.join(',') || undefined,
      work_styles: workStyleIds && workStyleIds.length && workStyleIds?.join(',') || undefined,
      category_ids: categoryIds && categoryIds.length && categoryIds?.join(',') || undefined,
      prefecture_ids: prefectureIds && prefectureIds.length && prefectureIds?.join(',') || undefined,
      skill_ids: skillIds && skillIds.length && skillIds?.join(',') || undefined,
      appeal_ids: appealIds && appealIds.length && appealIds?.join(',') || undefined,
      order: newOrder,
      page: newPage ? String(newPage) : undefined
    }

    const searchParams = Object.entries(params).filter(([_, value]) => value !== undefined)
    const urlSearchParams = new URLSearchParams(searchParams)

    if (options?.willUpdateDataLayer) {
      updateDataLayer(categoryIds, prefectureIds, skillIds, newMinPrice, workingDayIds)
    }

    navigate(`/job_offers?${urlSearchParams}`)
  }

  const onConditionChange = (
    newOpenonly?: boolean,
    newOrder?: DisplayOrder,
    newPage?: number,
  ) => {
    const latestParams = new URLSearchParams(location.search)

    word.current = latestParams.get('word') || undefined
    minPrice.current = parseInt(latestParams.get('min_price')) || undefined
    maxPrice.current = parseInt(latestParams.get('max_price')) || undefined
    openonly.current = newOpenonly !== undefined ? newOpenonly : latestParams.get('openonly') === null || latestParams.get('openonly') === '' || latestParams.get('openonly') === 'true'
    setWorkingDaysFromQuery(latestParams)
    setWorkStylesFromQuery(latestParams)
    selectedCategoryIds.current = latestParams.get('category_ids') ? latestParams.get('category_ids').split(',').map((str) => parseInt(str)) : categoryId ? [Number(categoryId)] : []
    selectedPrefectureIds.current = latestParams.get('prefecture_ids') ? latestParams.get('prefecture_ids').split(',').map((str) => parseInt(str)) : prefectureId ? [Number(prefectureId)] : []
    selectedSkillIds.current = latestParams.get('skill_ids') ? latestParams.get('skill_ids').split(',').map((str) => parseInt(str)) : skillId ? [Number(skillId)] : []
    selectedAppealIds.current = latestParams.get('appeal_ids') ? latestParams.get('appeal_ids').split(',').map((str) => parseInt(str)) : []
    order.current = newOrder as DisplayOrder || latestParams.get('order') as DisplayOrder || 'max_salary_desc'
    page.current = newPage || parseInt(latestParams.get('page')) || 1

    onSearch(
      word.current,
      minPrice.current,
      maxPrice.current,
      openonly.current,
      selectedWorkingDayIds.current,
      selectedWorkStyleIds.current,
      selectedCategoryIds.current,
      selectedPrefectureIds.current,
      selectedSkillIds.current,
      selectedAppealIds.current,
      order.current,
      page.current
    )
  }

  const setWorkingDaysFromQuery = (latestParams: URLSearchParams) => {
    selectedWorkingDayIds.current = latestParams.get('working_days') ? latestParams.get('working_days').split(',').map((str) => parseInt(str)) : workingDayId ? [Number(workingDayId)] : []

    setWorkingDays([
      { name: 'workingDayIds', label: '週1日', value: 1, defaultChecked: selectedWorkingDayIds.current?.length && selectedWorkingDayIds.current.includes(1) },
      { name: 'workingDayIds', label: '週2日', value: 2, defaultChecked: selectedWorkingDayIds.current?.length && selectedWorkingDayIds.current.includes(2) },
      { name: 'workingDayIds', label: '週3日', value: 3, defaultChecked: selectedWorkingDayIds.current?.length && selectedWorkingDayIds.current.includes(3) },
      { name: 'workingDayIds', label: '週4日', value: 4, defaultChecked: selectedWorkingDayIds.current?.length && selectedWorkingDayIds.current.includes(4) },
      { name: 'workingDayIds', label: '週5日', value: 5, defaultChecked: selectedWorkingDayIds.current?.length && selectedWorkingDayIds.current.includes(5) }
    ])
  }

  const setWorkStylesFromQuery = (latestParams: URLSearchParams) => {
    selectedWorkStyleIds.current = latestParams.get('work_styles') ? latestParams.get('work_styles').split(',').map((str) => parseInt(str)) : workStyleId ? [Number(workStyleId)] : []
    setWorkStyles([
      { name: 'workStyleIds', label: 'フルリモート', value: 2, defaultChecked: selectedWorkStyleIds.current?.length && selectedWorkStyleIds.current.includes(2) },
      { name: 'workStyleIds', label: '一部リモート', value: 3, defaultChecked: selectedWorkStyleIds.current?.length && selectedWorkStyleIds.current.includes(3) },
      { name: 'workStyleIds', label: '常駐', value: 1, defaultChecked: selectedWorkStyleIds.current?.length && selectedWorkStyleIds.current.includes(1) },
    ])
  }

  const createUserSearchingHistory = (
    word: string,
    minPrice: number,
    maxPrice: number,
    workingDays: number[],
    workStyles: number[],
    categoryIds: number[],
    prefectureIds: number[],
    skillIds: number[],
    appealIds: number[]
  ) => {
    void API.createUserSearchingHistory({
      word,
      minPrice,
      maxPrice,
      workingDays,
      workStyles,
      categoryIds,
      prefectureIds,
      skillIds,
      appealIds
    })
  }

  // 案件一覧情報の取得
  const fetchUserJobOffers = async () => {
    try {
      setLoading(true)

      const response = await API.getUserJobOffers(
        word.current,
        minPrice.current,
        maxPrice.current,
        openonly.current,
        selectedWorkingDayIds.current,
        selectedWorkStyleIds.current,
        selectedCategoryIds.current,
        selectedPrefectureIds.current,
        selectedSkillIds.current,
        selectedAppealIds.current,
        order.current,
        page.current,
      )
      const headers = response.headers
      setCount(headers['x-total-count'] as number)
      setPageCount(headers['x-total-pages'] as number)
      userJobOffers.current = response.data
    } catch (error) {
      if (API.isAxiosError(error)) {
        if (error.response && error.response.status === 404) {
          setCount(0)
          setPageCount(0)
          userJobOffers.current = []
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const updateJobOffersBySession = () => {
    if (!userJobOffers.current || loading) { return }

    setJobOffers(decorate(userJobOffers.current, session))
  }

  // 職種一覧の取得
  const fetchJobOffersCategories = async () => {
    const response = await API.getJobOffersCategories()
    const json = response.data
    const items: StyledSearchConditionDropdownPropsWithId[] = json.map((item: { id: number, name: string, categories: JobOfferCategory[] }) => {
      return {
        id: item.id,
        header: item.name,
        options: item.categories.map((item: JobOfferCategory) => {
          return {
            name: 'jobOfferCategoryIds',
            label: item.name,
            value: item.id,
            defaultChecked: selectedCategoryIds.current?.length && selectedCategoryIds.current.includes(item.id),
          }
        }),
        columns: 3,
      }
    })
    setCategories(items)
  }

  // エリア一覧の取得
  const fetchPrefectures = async () => {
    const response = await API.getPrefectures()
    const json = response.data
    const formatOption = (items: { id: number, name: string }[], startId: number, endId: number): CheckBoxProps[] => {
      return items
        .filter(
          (item: { id: number, name: string }) => { return item.id >= startId && item.id <= endId }
        )
        .map(
          (item: { id: number, name: string }) => {
            return {
              name: 'prefectureIds',
              label: item.name,
              value: item.id,
              defaultChecked: selectedPrefectureIds.current?.length && selectedPrefectureIds.current.includes(item.id),
            }
          }
        )
    }
    // NOTE: 本番DBに都道府県コード通りに登録されているため、番号で分類
    // https://www.meti.go.jp/policy/chemical_management/kasinhou/files/ippantou/table_prefecturecode.pdf
    const hokkaidoTohoku: CheckBoxProps[] = formatOption(json, 1, 7)
    const kanto: CheckBoxProps[] = formatOption(json, 8, 14)
    const hokushinetsuTokai: CheckBoxProps[] = formatOption(json, 15, 24)
    const kansai: CheckBoxProps[] = formatOption(json, 25, 30)
    const chugokuShikoku: CheckBoxProps[] = formatOption(json, 31, 39)
    const kyushuOkinawa: CheckBoxProps[] = formatOption(json, 40, 47)
    const categorizedPrefectures: StyledSearchConditionDropdownProps[] = [
      {
        header: '北海道・東北',
        options: hokkaidoTohoku,
        columns: 3,
      },
      {
        header: '関東',
        options: kanto,
        columns: 3,
      },
      {
        header: '北信越・東海',
        options: hokushinetsuTokai,
        columns: 3,
      },
      {
        header: '関西',
        options: kansai,
        columns: 3,
      },
      {
        header: '中国・四国',
        options: chugokuShikoku,
        columns: 3,
      },
      {
        header: '九州・沖縄',
        options: kyushuOkinawa,
        columns: 3,
      },
    ]
    setPrefectures(categorizedPrefectures)
  }

  // スキル一覧の取得
  const fetchSkills = async () => {
    const response = await API.getSkillCategories()
    const json = response.data
    const items: StyledSearchConditionDropdownProps[] = json.map((category: SkillCategory) => {
      const options: Array<CheckBoxProps> = category.skills &&
        category.skills.map((skill: Skill) => {
          return {
            name: 'skillIds',
            label: skill.skill_name,
            value: skill.id,
            defaultChecked: selectedSkillIds.current?.length && selectedSkillIds.current.includes(skill.id),
          }
        })
      const groupedOptions: Array<OptionGroup> = category.types &&
        category.types.map((type: SkillType) => {
          return {
            title: type.type_name,
            options: type.skills.map((skill: Skill) => {
              return {
                name: 'skillIds',
                label: skill.skill_name,
                value: skill.id,
                defaultChecked: selectedSkillIds.current?.length && selectedSkillIds.current.includes(skill.id),
              }
            })
          }
        })
      return {
        header: category.category_name,
        columns: 2,
        options: options,
        groupedOptions: groupedOptions,
      }
    })
    setSkills(items)
  }

  // こだわり一覧の取得
  const fetchAppeals = async () => {
    const response = await API.getAppeals()
    const json = response.data
    const items: CheckBoxProps[] = json.map((item: { id: number, description: string }) => {
      return {
        name: 'appealIds',
        label: item.description,
        value: item.id,
        defaultChecked: selectedAppealIds.current?.length && selectedAppealIds.current.includes(item.id),
      }
    })
    setAppeals(items)
  }

  // 他の職種で探すの職種一覧の取得
  const fetchRecommendedCategories = async () => {
    const response = await API.getRecommendedCategories()
    const json: JobOfferCategory[] = response.data
    const items = json.map((item: JobOfferCategory) => {
      return {
        label: item.name,
        id: item.id,
      }
    })
    setRecommendedCategories({
      title: '他の職種で探す',
      category_abbreviation: 'o',
      queryItems: items,
    })
  }

  // 他のスキルで探すのスキル一覧の取得
  const fetchRecommendedSkills = async () => {
    const response = await API.getRecommendedSkills()
    const json: MasterSkill[] = response.data
    const items = json.map((item: MasterSkill) => {
      return {
        label: item.name,
        id: item.id,
      }
    })
    setRecommendedSkills({
      title: '他のスキルで探す',
      category_abbreviation: 's',
      queryItems: items,
    })
  }

  const location = useLocation()

  const restoreParamsFromQuery = () => {
    const searchParams = new URLSearchParams(location.search)

    word.current = searchParams.get('word') || undefined
    minPrice.current = parseInt(searchParams.get('min_price')) || undefined
    maxPrice.current = parseInt(searchParams.get('max_price')) || undefined
    openonly.current = searchParams.get('openonly') === null || searchParams.get('openonly') === '' || searchParams.get('openonly') === 'true'
    setWorkingDaysFromQuery(searchParams)
    setWorkStylesFromQuery(searchParams)
    selectedCategoryIds.current = searchParams.get('category_ids') ? searchParams.get('category_ids').split(',').map((str) => parseInt(str)) : categoryId ? [Number(categoryId)] : []
    selectedPrefectureIds.current = searchParams.get('prefecture_ids') ? searchParams.get('prefecture_ids').split(',').map((str) => parseInt(str)) : prefectureId ? [Number(prefectureId)] : []
    selectedSkillIds.current = searchParams.get('skill_ids') ? searchParams.get('skill_ids').split(',').map((str) => parseInt(str)) : skillId ? [Number(skillId)] : []
    selectedAppealIds.current = searchParams.get('appeal_ids') ? searchParams.get('appeal_ids').split(',').map((str) => parseInt(str)) : appealId ? [Number(appealId)] : []
    order.current = searchParams.get('order') as DisplayOrder || 'max_salary_desc'
    page.current = parseInt(searchParams.get('page')) || 1
  }

  useEffect(() => {
    restoreParamsFromQuery()
    void fetchUserJobOffers()
    void fetchJobOffersSeo()
  }, [location.search])

  useEffect(() => {
    void fetchJobOffersCategories()
    void fetchPrefectures()
    void fetchSkills()
    void fetchAppeals()
    void fetchRecommendedCategories()
    void fetchRecommendedSkills()
  }, [])

  useEffect(() => {
    updateJobOffersBySession()
  }, [session, loading])

  const props: StyledJobOffersProps = {
    pageTitle: h1,
    word: word.current,
    minPrice: minPrice.current,
    maxPrice: maxPrice.current,
    openonly: openonly.current,
    order: order.current,
    page: page.current,
    workingDays: workingDays,
    workStyles: workStyles,
    jobOfferCategories: categories,
    prefectures: prefectures,
    skills: skills,
    breadcrumbs: breadcrumb,
    appeals: appeals,
    onSearch: onSearch,
    onConditionChange: onConditionChange,
    jobOffers: jobOffers,
    count: count,
    pageCount: pageCount,
    loggedOut: loggedOut,
    searchWithOtherQueryList: [
      recommendedCategories,
      recommendedSkills,
    ],
    createUserSearchingHistory: createUserSearchingHistory,
    seoContentCategory: seoContentCategory,
    seoContentMasterSkill: seoContentMasterSkill,
    selectedCategoryIds: selectedCategoryIds.current,
    selectedSkillIds: selectedSkillIds.current,
    selectedPrefectureIds: selectedPrefectureIds.current,
    selectedAppealIds: selectedAppealIds.current,
  }

  return (
    <MemberLayout>
      <Head
        title={pageTitle}
        description={pageDescription}
        canonical={canonicalUrl}
        prevUrl={prevUrl}
        nextUrl={nextUrl}
      />
      <StyledLoadingOverlay isOpen={loading} />
      <StyledJobOffers {...props} />
    </MemberLayout>
  )
}

export default Index
